import { useState } from "react";
import clsx from "clsx";
import { ChevronDownIcon } from "lucide-react";

import { JobStatusTransitionConfirmationDialog } from "@/components";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { useUpdateTargetJob } from "@/hooks/mutations";
import {
  jobStatusOptions as allJobStatusOptions,
  jobStatusOptionsForNonActiveUpdate,
  jobStatusToBackgroundColor,
  jobStatusToLabel,
} from "@/lib/constants";
import { Job, JobStatus } from "@/types";

export const JobStatusDropdown = ({
  job,
  setJob,
}: {
  job: Job;
  setJob: ({ job }: { job: Job }) => void;
}) => {
  const { mutate: updateTargetJob } = useUpdateTargetJob();
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
    useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<JobStatus>(job.status);

  // TODO: Improve this.
  // - If we are updating a job that isn't active, we show: REVIEW, DRAFT, CLOSED.
  // - If we are updating a job that is active, we show: ACTIVE, REVIEW, DRAFT, CLOSED.
  const jobStatusOptions =
    job.status !== JobStatus.ACTIVE
      ? jobStatusOptionsForNonActiveUpdate
      : allJobStatusOptions;

  const handleClick = (status: JobStatus) => {
    if (status === job.status) {
      return;
    }

    setSelectedStatus(status);
    setIsConfirmationDialogOpen(true);
  };

  const handleConfirm = () => {
    updateTargetJob(
      {
        request: {
          ...job,
          status: selectedStatus,
        },
        targetJobId: job.id,
      },
      {
        onSuccess: (data) => {
          // Update the job in the relevant query cache.
          setJob({ job: data });

          // Close the dialog.
          setIsConfirmationDialogOpen(false);
        },
      },
    );
  };

  return (
    <>
      <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
        <DropdownMenuTrigger asChild>
          <Button className="rounded-full" variant="outlineSecondary">
            <div
              className={clsx(
                "h-3 w-3 rounded-full",
                jobStatusToBackgroundColor[job.status],
              )}
            />
            {jobStatusToLabel[job.status]}
            <ChevronDownIcon className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {jobStatusOptions.map((option) => (
            <DropdownMenuCheckboxItem
              key={option.value}
              checked={job.status === option.value}
              onClick={(e) => {
                // So the table row doesn't get clicked if we are using
                // the dropdown within the context of the table.
                e.stopPropagation();
                handleClick(option.value);
              }}
            >
              {option.label}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <JobStatusTransitionConfirmationDialog
        jobStatus={selectedStatus}
        onConfirm={handleConfirm}
        onOpenChange={setIsConfirmationDialogOpen}
        open={!isDropdownOpen && isConfirmationDialogOpen} // https://github.com/shadcn-ui/ui/issues/1912#issuecomment-2197220521
        preventContentClickPropagation
        preventOverlayClickCapturePropagation
      />
    </>
  );
};
